import { useEffect, useState } from 'react';
import { USERNAME_PATTERN } from '@shared/utils/username';
import { checkUsernameExists } from '../services/profileService';

export const USERNAME_CHECK_DEBOUNCE_MS = 500;

/**
 * Live "is this username free?" check for the signed-in user's own profile — the Profile
 * counterpart of the Edit User drawer's check on the Team page.
 *
 * The status goes to 'checking' as soon as the value changes, not only once the debounce fires, so
 * Save stays blocked for the whole window rather than letting a quick click race the lookup. The
 * user's current username never counts as taken, compared case-insensitively because the lookup
 * is case-insensitive and would otherwise match the user's own row. A failed lookup clears the
 * status instead of blocking: the server re-checks on save either way.
 *
 * An empty (or whitespace-only) value is reported as 'required' straight away, rather than waiting
 * for the form's own validation, which only shows its message after a Save attempt. A value with
 * characters the server rejects (spaces, symbols) is reported as 'invalid' and never looked up —
 * the availability endpoint would call it "available", and the save would then fail with the
 * server's generic "Validation failed." instead of saying what is wrong.
 *
 * @param {string} username - Value currently in the Username field
 * @param {string} originalUsername - The user's saved username
 * @returns {null|'required'|'invalid'|'checking'|'available'|'taken'} null when there is nothing to check
 */
export function useUsernameAvailability(username, originalUsername) {
  // The last finished lookup, tagged with the value it was for. Everything else is derived during
  // render, so the effect never sets state synchronously.
  const [lookup, setLookup] = useState({ value: null, status: null });

  // Untrimmed on purpose for the format test: a leading/trailing space is still a space the server
  // rejects. Trimmed for everything else, so " alice " is not mistaken for a new name.
  const raw = username ?? '';
  const value = raw.trim();
  const unchanged = value.toLowerCase() === (originalUsername ?? '').trim().toLowerCase();
  const isValidFormat = USERNAME_PATTERN.test(raw);
  const shouldCheck = value !== '' && !unchanged && isValidFormat;

  useEffect(() => {
    if (!shouldCheck) return undefined;

    // Guards against an out-of-order response: only the lookup for the latest value may land.
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const exists = await checkUsernameExists(value);
        if (!cancelled) setLookup({ value, status: exists ? 'taken' : 'available' });
      } catch {
        if (!cancelled) setLookup({ value, status: null });
      }
    }, USERNAME_CHECK_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, shouldCheck]);

  if (value === '') return 'required';
  if (!isValidFormat) return 'invalid';
  if (!shouldCheck) return null;
  return lookup.value === value ? lookup.status : 'checking';
}
